<template>
  <div class="budget-dashboard">
    <section v-if="summary" class="card summary-card">
      <h2 class="interval-title">Интервал ATI: <code>{{ summary.interval_id }}</code></h2>
      <div class="metrics-grid">
        <div class="metric">
          <span class="metric-label">Лимит интервала</span>
          <strong class="metric-value">{{ summary.interval_limit }}</strong>
        </div>
        <div v-for="b in bucketRows" :key="b.name" class="metric" :class="'metric-' + b.name">
          <span class="metric-label">{{ b.name }}</span>
          <strong class="metric-value">{{ b.used }} / {{ b.limit }}</strong>
          <div class="bar">
            <div class="bar-fill" :style="{ width: b.pct + '%' }" :class="'bar-' + b.name"></div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="byStatusRows.length" class="card">
      <h3>Статусы по бакетам</h3>
      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>Бакет</th>
              <th>Статус</th>
              <th>Количество</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in byStatusRows" :key="i">
              <td>{{ r.bucket }}</td>
              <td><span class="badge" :class="statusClass(r.status)">{{ r.status }}</span></td>
              <td>{{ r.count }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="card">
      <div class="priority-head">
        <h3>Приоритетные маршруты <span v-if="priority" class="count">({{ priority.total }})</span></h3>
        <button class="btn-secondary" :disabled="loading" @click="load">
          {{ loading ? 'Обновляем...' : 'Обновить' }}
        </button>
      </div>
      <div v-if="hasError" class="error-message">Не удалось загрузить данные бюджета.</div>
      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>Направление</th>
              <th>Расстояние</th>
              <th>Приоритет</th>
              <th>Источник</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading && !priorityItems.length">
              <td colspan="4" class="muted center">Загрузка...</td>
            </tr>
            <tr v-else-if="!priorityItems.length">
              <td colspan="4" class="muted center">Нет приоритетных маршрутов</td>
            </tr>
            <tr v-for="row in priorityItems" :key="row.ati_route_id">
              <td>
                <div class="dir">{{ row.departure_city }} → {{ row.destination_city }}</div>
                <div class="dir-id">№ {{ row.ati_route_id }}</div>
              </td>
              <td>{{ row.distance ? row.distance + ' км' : '—' }}</td>
              <td>{{ row.priority }}</td>
              <td>{{ row.source || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { getRateLimitUsageSummary, getRateLimitPriorityList } from '@/backend/atiPriceCheckApi'
import { useErrorHandler } from '@/hooks/errorHandler'

export default {
  name: 'BudgetDashboard',
  setup() {
    const summary = ref(null)
    const priority = ref(null)
    const loading = ref(false)
    const hasError = ref(false)

    const KNOWN_BUCKETS = ['priority', 'fact', 'reserve']

    const bucketRows = computed(() => {
      if (!summary.value?.buckets) return []
      return Object.entries(summary.value.buckets).map(([name, info]) => {
        const limit = info.limit || 0
        const used = info.used || 0
        return {
          name,
          limit,
          used,
          pct: limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0,
        }
      })
    })

    const priorityItems = computed(() => priority.value?.items || [])

    const byStatusRows = computed(() => {
      if (!summary.value?.buckets) return []
      const rows = []
      for (const [bucket, info] of Object.entries(summary.value.buckets)) {
        for (const [status, count] of Object.entries(info.by_status || {})) {
          rows.push({ bucket, status, count })
        }
      }
      return rows
    })

    const statusClass = (status) => {
      if (status === 'success') return 'badge-ok'
      if (status === 'not_found') return 'badge-warn'
      if (status === 'budget_exhausted') return 'badge-budget'
      if (status === 'error' || status === 'transient_error') return 'badge-err'
      return 'badge-muted'
    }

    const load = async () => {
      loading.value = true
      hasError.value = false
      try {
        const [s, p] = await Promise.all([
          getRateLimitUsageSummary(),
          getRateLimitPriorityList(),
        ])
        summary.value = s
        priority.value = p
      } catch (err) {
        hasError.value = true
        useErrorHandler(err)
      } finally {
        loading.value = false
      }
    }

    onMounted(load)

    return {
      summary,
      priority,
      priorityItems,
      bucketRows,
      byStatusRows,
      loading,
      hasError,
      statusClass,
      load,
    }
  },
}
</script>

<style scoped>
.budget-dashboard {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.card {
  padding: 20px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 22px rgba(30, 50, 90, 0.08);
}

.interval-title {
  margin: 0 0 14px;
  color: #1d2b44;
}

.interval-title code {
  background: #eef1f6;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 14px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
}

.metric {
  padding: 14px;
  border-radius: 11px;
  background: #f8fbff;
  border: 1px solid #e1e8f1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.metric-label {
  font-size: 13px;
  color: #56657a;
  text-transform: capitalize;
}

.metric-value {
  font-size: 20px;
  color: #1d2b44;
}

.bar {
  height: 6px;
  border-radius: 4px;
  background: #e6ecf4;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 4px;
}

.bar-priority { background: #3a3f9c; }
.bar-fact { background: #2c5e93; }
.bar-reserve { background: #2d8a57; }

h3 {
  margin: 0 0 14px;
  color: #1d2b44;
}

.priority-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.priority-head h3 {
  margin: 0;
}

.count {
  color: #56657a;
  font-weight: 400;
}

.btn-secondary {
  padding: 8px 14px;
  border: 1px solid #c3ccdb;
  border-radius: 9px;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  text-align: left;
  padding: 10px;
  background: #f4f7fc;
  color: #42506a;
  font-weight: 600;
  border-bottom: 1px solid #e1e8f1;
}

.data-table td {
  padding: 10px;
  border-bottom: 1px solid #eef1f6;
  color: #1d2b44;
  vertical-align: top;
}

.dir {
  font-weight: 600;
}

.dir-id {
  font-size: 12px;
  color: #9aa6b5;
}

.muted {
  color: #9aa6b5;
}

.center {
  text-align: center;
}

.badge {
  padding: 2px 8px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 600;
}

.badge-ok { background: #e3f6ec; color: #1d7a47; }
.badge-warn { background: #fdf0e3; color: #a85a16; }
.badge-budget { background: #fdecec; color: #a52828; }
.badge-err { background: #f6e9f5; color: #8c3270; }
.badge-muted { background: #eef1f6; color: #6b7686; }

.error-message {
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 9px;
  background: #fdecec;
  color: #a52828;
}
</style>
